<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { ReferencesApi } from '@/modules/documents-new/services/referencesApi'
import type { DocumentType, DocumentTypeWritePayload } from '@/modules/documents-new/types/document.types'
import { useNotifications } from '@/modules/feedback/composables/useNotifications'

interface Props {
  isOpen: boolean
  documentType?: DocumentType | null
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'saved', value: DocumentType): void
}

const props = withDefaults(defineProps<Props>(), {
  documentType: null,
})

const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const { showSuccess, showError } = useNotifications()

const formRef = ref()
const isSaving = ref(false)
const name = ref('')
const description = ref('')

const isEditMode = computed(() => !!props.documentType?.id)
const title = computed(() => (isEditMode.value ? 'Редактировать вид документа' : 'Добавить вид документа'))
const submitLabel = computed(() => (isEditMode.value ? 'Сохранить' : 'Создать'))

const rules = {
  name: [
    (v: string) => !!v?.trim() || 'Наименование обязательно',
    (v: string) => (v?.trim().length ?? 0) <= 255 || 'Не больше 255 символов',
  ],
}

const resetForm = () => {
  name.value = props.documentType?.name ?? ''
  description.value = props.documentType?.description ?? ''
}

const closeModal = () => {
  if (isSaving.value) {
    return
  }

  emit('update:isOpen', false)
}

watch(
  () => props.isOpen,
  async open => {
    if (!open) {
      return
    }

    resetForm()
    await nextTick()
    formRef.value?.resetValidation()
  },
)

const buildPayload = (): DocumentTypeWritePayload => ({
  name: name.value.trim(),
  description: description.value.trim(),
})

const onSubmit = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    return
  }

  isSaving.value = true
  try {
    const payload = buildPayload()
    const saved = isEditMode.value && props.documentType
      ? await ReferencesApi.updateType(props.documentType.id, payload)
      : await ReferencesApi.createType(payload)

    showSuccess(isEditMode.value ? 'Вид документа сохранён' : 'Вид документа создан')
    emit('saved', saved)
    emit('update:isOpen', false)
  } catch (err) {
    showError(err, isEditMode.value ? 'Редактирование вида документа' : 'Создание вида документа')
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    :max-width="smAndDown ? '100%' : 560"
    :fullscreen="smAndDown"
    persistent
    @update:model-value="emit('update:isOpen', $event)"
  >
    <VCard :class="smAndDown ? 'pa-4' : 'pa-6'">
      <div class="d-flex justify-space-between align-center mb-6">
        <span class="text-h5 font-weight-bold">{{ title }}</span>
        <CustomCloseButton :close-action="closeModal" />
      </div>

      <VForm
        ref="formRef"
        @submit.prevent="onSubmit"
      >
        <VTextField
          v-model="name"
          variant="outlined"
          density="compact"
          class="mb-4"
          :rules="rules.name"
          maxlength="255"
          autofocus
        >
          <template #label>
            <span>Наименование<sup class="required-label">*</sup></span>
          </template>
        </VTextField>

        <VTextarea
          v-model="description"
          variant="outlined"
          density="compact"
          class="mb-4"
          rows="3"
          auto-grow
        >
          <template #label>
            <span>Описание</span>
          </template>
        </VTextarea>

        <div class="d-flex justify-end gap-4 mt-6">
          <VBtn
            variant="outlined"
            color="grey"
            class="px-6"
            :disabled="isSaving"
            @click="closeModal"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="px-6"
            :loading="isSaving"
            @click="onSubmit"
          >
            {{ submitLabel }}
          </VBtn>
        </div>
      </VForm>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.required-label {
  color: rgb(var(--v-theme-error));
  margin-inline-start: 2px;
}

:deep(.v-card) {
  border-radius: 12px;
}

:deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
}
</style>
